import React, {useEffect, useState} from 'react';
import {makeRequest} from "../../utils/httpRequest";
import {useRecoilState} from "recoil";
import {loadingAtom} from "../../atoms";
import Config from "../../core/config";
import ActionButtonsManagerView from "../common/ActionButtonsManagerView";
import ConfirmButton from "../common/ConfirmButton";
import {useNavigate} from "react-router-dom";
import {
    findCompaniesByQueryString,
    getCompanyById,
    getCompanyOfCurrentUser,
    isUserAdmin
} from "../../utils/requestUtils";
import {UserPermission} from "../../utils/utils";
import Loading from "../../core/Loading";
import {usePermission} from "../../core/PermissionContext";
import { showConfirm, showAlert } from '../../core/dialog';

const apiUrl = Config.apiUrl;

/** ISO-8601: 1 = segunda … 7 = domingo; vazio = todos os dias */
const WORK_ACCESS_WEEKDAY_OPTIONS = [
    { value: '', label: 'Todos os dias' },
    { value: '1', label: 'Segunda-feira' },
    { value: '2', label: 'Terça-feira' },
    { value: '3', label: 'Quarta-feira' },
    { value: '4', label: 'Quinta-feira' },
    { value: '5', label: 'Sexta-feira' },
    { value: '6', label: 'Sábado' },
    { value: '7', label: 'Domingo' },
];

const UserManager = () => {
    const {hasUserPermission, loadingPermissions} = usePermission();
    const [, setLoading] = useRecoilState(loadingAtom);
    const navigate = useNavigate();
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');

    const url = window.location.pathname;
    let admin = false;
    if (url.startsWith('/admin')) {
        admin = true
    }

    const newEntity = {
        id: undefined,
        name: '',
        email: '',
        permissions: [],
        password: '',
        workAccessPeriods: [],
        enabled: true,
    };

    const [formData, setFormData] = useState(newEntity);
    const [companyScheduleTemplate, setCompanyScheduleTemplate] = useState(null);
    const [availablePermissions, setAvailablePermissions] = useState([]);
    const [originalPermissions, setOriginalPermissions] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [userCompany, setUserCompany] = useState(undefined);
    const [errorMessage, setErrorMessage] = useState('');
    const [permissionFilter, setPermissionFilter] = useState('');

    // Add this state for select all
    const allPermissionsSelected = Object.keys(availablePermissions).length > 0 && Object.keys(availablePermissions).every((permission) => formData.permissions.includes(permission));
    const handleSelectAllPermissions = (e) => {
        const checked = e.target.checked;
        setFormData((prevData) => ({
            ...prevData,
            permissions: checked ? Object.keys(availablePermissions) : []
        }));
    };

    // Clear error message when form data changes
    useEffect(() => {
        setErrorMessage('');
    }, [formData.email]);

    useEffect(() => {
        fetchPermissions();
        if (url.startsWith('/admin')) {
            isUserAdmin().then(userAdmin => {
                if (!userAdmin)
                    navigate('/unauthorizedaccess');
            });
        }
    }, []);

    useEffect(() => {
        if (formData.companyId !== undefined) {
            getCompanyById(formData.companyId).then(response => setUserCompany(response));
        } else {
            setUserCompany(undefined);
        }
    }, [formData.companyId]);

    useEffect(() => {

        const filteredPermissions = Object.entries(originalPermissions)
            .filter(([key, value]) =>
                userCompany.permissions.some(up => up === key)
            )
            .map(([key, value]) => key);

        const newAvailablePermissions = filteredPermissions.reduce((acc, key) => {
            acc[key] = originalPermissions[key];
            return acc;
        }, {});

        console.log(filteredPermissions);
        setAvailablePermissions(newAvailablePermissions);
    }, [userCompany]);

    // Filtrar permissões baseado no texto de busca
    const filteredPermissions = Object.entries(availablePermissions).filter(([permission, values]) =>
        values.description.toLowerCase().includes(permissionFilter.toLowerCase()) ||
        permission.toLowerCase().includes(permissionFilter.toLowerCase())
    );

    const handleChange = (e) => {
        const {name, value, type, checked} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePermissionChange = (e) => {
        const {value, checked} = e.target;
        setFormData((prevData) => ({
            ...prevData,
            permissions: checked
                ? [...prevData.permissions, value]
                : prevData.permissions.filter(permission => permission !== value)
        }));
    };

    async function fetchPermissions() {
        try {
            setLoading(true);
            const response = await makeRequest(apiUrl + '/api/userpermission/users_permission_info', 'GET');

            const permissionsMap = response.reduce((acc, permission) => {
                acc[permission.permission] = {description: permission.description, isChecked: false, isEnabled: true};
                return acc;
            }, {});
            setOriginalPermissions({...permissionsMap});
            setAvailablePermissions({...permissionsMap});
        } catch (error) {
            console.error('Error fetching permissions:', error);
        } finally {
            setLoading(false);
        }
    }


    async function makeQuery() {
        if (id) {
            try {
                setLoading(true);
                const url = apiUrl + '/api/users/id=' + id;
                const response = await makeRequest(url, 'GET');
                setFormData({
                    ...response,
                    workAccessPeriods: (response.workAccessPeriods || []).map((p) => ({
                        startTime: (p.startTime || '').toString().slice(0, 5),
                        endTime: (p.endTime || '').toString().slice(0, 5),
                        dayOfWeek: p.dayOfWeek != null && p.dayOfWeek !== '' ? Number(p.dayOfWeek) : null,
                    })),
                });
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        } else {
            setFormData(newEntity);
        }
    }

    useEffect(() => {
        // Admin lista empresas via /api/companies/query= (exige LIST_COMPANY ou equivalente).
        // Usuário comum não tem essa permissão — o backend respondia 401 sem corpo e o cliente
        // interpretava como AUTH_TOKEN_INVALID. Para não-admin, carrega só a empresa atual.
        if (admin) {
            findCompaniesByQueryString("").then((list) => setCompanies(list || []));
        } else {
            getCompanyOfCurrentUser()
                .then((c) => setCompanies(c ? [c] : []))
                .catch(() => setCompanies([]));
        }
        makeQuery();
    }, [id]);

    // Cadastro não-admin: define companyId da empresa logada para filtrar permissões (não há seletor de empresa).
    useEffect(() => {
        if (admin || id) return;
        const c = companies[0];
        if (!c) return;
        setFormData((prev) => {
            if (prev.companyId != null) return prev;
            return { ...prev, companyId: c.id };
        });
    }, [admin, id, companies]);

    useEffect(() => {
        const loadCompanyTemplate = async () => {
            try {
                if (admin && formData.companyId) {
                    const c = await getCompanyById(formData.companyId);
                    setCompanyScheduleTemplate(c);
                } else if (!admin) {
                    const c = await getCompanyOfCurrentUser();
                    setCompanyScheduleTemplate(c);
                } else {
                    setCompanyScheduleTemplate(null);
                }
            } catch (e) {
                setCompanyScheduleTemplate(null);
            }
        };
        loadCompanyTemplate();
    }, [admin, formData.companyId]);

    const handleWorkAccessPeriodChange = (index, field, value) => {
        setFormData((prevData) => {
            const periods = [...(prevData.workAccessPeriods || [])];
            if (!periods[index]) {
                return prevData;
            }
            periods[index] = {...periods[index], [field]: value};
            return {...prevData, workAccessPeriods: periods};
        });
    };

    const handleAddWorkAccessPeriod = () => {
        setFormData((prevData) => ({
            ...prevData,
            workAccessPeriods: [...(prevData.workAccessPeriods || []), { startTime: '09:00', endTime: '18:00', dayOfWeek: null }],
        }));
    };

    const handleRemoveWorkAccessPeriod = (index) => {
        setFormData((prevData) => ({
            ...prevData,
            workAccessPeriods: (prevData.workAccessPeriods || []).filter((_, i) => i !== index),
        }));
    };

    const onConfirm = async () => {
        try {
            if (validateInputs()) {
                setLoading(true);
                setErrorMessage(''); // Clear any previous error messages
                await new Promise(resolve => setTimeout(resolve, 300));
                await saveEntity();
                navigate(`/${admin ? 'admin' : 'app'}/userlist`);
            }
        } catch (error) {
            console.error('Error saving:', error);
            showAlert('Error ao salvar.');
        } finally {
            setLoading(false);
        }
    };

    function validateInputs() {
        if (!formData.name || formData.name === '') {
            showAlert('Preencha o campo nome.');
            return false;
        }
        if (!formData.email || formData.email === '') {
            showAlert('Preencha o campo e-mail.');
            return false;
        }
        return true;
    }

    async function saveEntity() {
        const url = apiUrl + '/api/users';
        setLoading(true);
        try {
            const response = await makeRequest(url, id ? 'PUT' : 'POST', {
                'Content-Type': 'application/json',
            }, JSON.stringify(formData));

            // If we get here, the request was successful
            return response;
        } catch (error) {
            console.log(error);
            if (error.statusCode === 409) {
                // Duplicate email error
                setErrorMessage('E-mail já cadastrado no sistema. Por favor, utilize um e-mail diferente.');
                throw error;
            } else {
                setErrorMessage('Erro ao salvar usuário. Tente novamente.');
                throw error;
            }
        } finally {
            setLoading(false);
        }
    }


    function handleCompanySelected(value) {

        const company = value !== undefined ? companies.find((p) => p.id === Number(value)) : undefined;
        setFormData((prevData) => ({
            ...prevData,
            companyId: company?.id
        }));

    }

    if (loadingPermissions) {
        return <Loading/>
    }

    if (!admin && (hasUserPermission(UserPermission.CREATE_USER) === false || (hasUserPermission(UserPermission.EDIT_USER) === false && id))) {
        navigate('/unauthorizedaccess');
    }

    return (
        <div className="app-form-page app-form-page--narrow">
            <div className="header-label">{formData.id > 0 ? 'Editar' : 'Cadastrar'} Usuário</div>

            {admin &&
                <>
                    <div style={{marginTop: '5px', alignItems: 'center'}}>
                        <div>Empresa:</div>
                        <select data-test="company-select" value={formData.companyId || ''}
                                onChange={e => handleCompanySelected(e.target.value)}>
                            <option value=''>Selecione uma empresa</option>
                            {companies.map(company => (
                                <option key={company.id} value={company.id}>{company.name}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{marginTop: '5px', alignItems: 'center'}}>
                        <div style={{height: '17px'}}>
                        </div>
                        <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
                            <label style={{display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'}}>
                                <input type="checkbox"
                                       checked={formData.adminUser}
                                       name="adminUser"
                                       onChange={handleChange}/>
                                <span>Administrador?</span>
                            </label>
                            <label style={{display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer'}}>
                                <input type="checkbox"
                                       checked={formData.enabled !== false}
                                       name="enabled"
                                       onChange={handleChange}/>
                                <span>Habilitado?</span>
                            </label>
                        </div>
                    </div>
                </>
            }

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Nome:</div>
                <input type="text" style={{width: '300px'}} value={formData.name} name="name"
                       autoComplete="off" onChange={handleChange}/>
            </div>

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>e-mail:</div>
                <input type="email" style={{width: '300px', textTransform: 'none'}} value={formData.email} name="email"
                       autoComplete="off" onChange={handleChange}/>
            </div>

            {errorMessage && (
                <div style={{marginTop: '5px', color: 'red', fontSize: '14px', textAlign: 'center'}}>
                    {errorMessage}
                </div>
            )}

            <div style={{marginTop: '5px', alignItems: 'center'}}>
                <div style={{width: '180px'}}>Senha:</div>
                <input type="password" style={{width: '300px', textTransform: 'none'}} value={formData.password}
                       name="password" autoComplete="off"
                       onChange={handleChange}/>
            </div>

            <div style={{marginTop: '12px', border: '1px solid #ddd', padding: '12px', borderRadius: '6px'}}>
                <div style={{fontWeight: 'bold', marginBottom: '8px'}}>Horário de acesso</div>
                <>
                    <div style={{marginTop: '6px', marginBottom: '4px'}}>Períodos específicos deste usuário:</div>
                    {(formData.workAccessPeriods || []).map((period, index) => (
                        <div key={`user-work-${index}`}
                             style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', gap: '8px', flexWrap: 'wrap' }}>
                            <select
                                aria-label="Dia da semana"
                                value={period.dayOfWeek == null || period.dayOfWeek === '' ? '' : String(period.dayOfWeek)}
                                onChange={(e) => handleWorkAccessPeriodChange(
                                    index,
                                    'dayOfWeek',
                                    e.target.value === '' ? null : parseInt(e.target.value, 10),
                                )}
                            >
                                {WORK_ACCESS_WEEKDAY_OPTIONS.map((o) => (
                                    <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                            <input
                                type="time"
                                value={(period.startTime || '').toString().slice(0, 5)}
                                onChange={(e) => handleWorkAccessPeriodChange(index, 'startTime', e.target.value)}
                            />
                            <span>até</span>
                            <input
                                type="time"
                                value={(period.endTime || '').toString().slice(0, 5)}
                                onChange={(e) => handleWorkAccessPeriodChange(index, 'endTime', e.target.value)}
                            />
                            <button type="button" onClick={() => handleRemoveWorkAccessPeriod(index)}>Remover</button>
                        </div>
                    ))}
                    <button type="button" style={{marginTop: '6px'}} onClick={handleAddWorkAccessPeriod}>Adicionar
                        período
                    </button>
                </>
            </div>
            <div style={{marginTop: '5px', overflowY: 'auto', maxHeight: '300px'}}>
                <div style={{width: '180px'}}>Permissões:</div>

                {/* Campo de filtro */}
                <div style={{marginBottom: '10px'}}>
                    <input
                        type="text"
                        placeholder="Filtrar permissões..."
                        value={permissionFilter}
                        onChange={(e) => setPermissionFilter(e.target.value)}
                        style={{width: '300px', padding: '5px'}}
                    />
                </div>

                {/* Select/Unselect All Checkbox */}
                {filteredPermissions.length > 0 && (
                    <div style={{display: 'flex', alignItems: 'center', marginBottom: 4}}>
                        <input
                            type="checkbox"
                            checked={allPermissionsSelected}
                            onChange={handleSelectAllPermissions}
                        />
                        <span style={{marginLeft: 8}}>Selecionar/Desmarcar todas</span>
                    </div>
                )}

                <div style={{maxHeight: '400px', overflowY: 'auto', border: '1px solid #ccc', padding: '10px'}}>
                    {admin && formData.companyId === undefined ?
                        <>Selecione uma empresa antes de definir as permissões.</>
                        :
                        (filteredPermissions.length > 0 ? (
                            filteredPermissions.map(([permission, values]) => (
                                <div key={permission}
                                     style={{display: "flex", alignItems: "center", marginBottom: '5px'}}>
                                    <label style={{display: "flex", alignItems: "center"}}>
                                        <input
                                            type="checkbox"
                                            value={permission}
                                            checked={formData.permissions.includes(permission)}
                                            onChange={handlePermissionChange}
                                        />
                                        <span style={{marginLeft: '8px'}}>
                                            {values.description}
                                        </span>
                                    </label>
                                </div>
                            ))
                        ) : (
                            <div style={{color: '#888', textAlign: 'center'}}>
                                {permissionFilter ? 'Nenhuma permissão encontrada com o filtro aplicado.' : 'Nenhuma permissão disponível.'}
                            </div>
                        ))}
                </div>
            </div>

            <ActionButtonsManagerView>
                <ConfirmButton onClick={onConfirm}/>
            </ActionButtonsManagerView>
        </div>
    );
};

export default UserManager;
